import { useEffect, useId, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fieldInputCls } from "@/components/Field";
import { useT } from "@/lib/i18n";

const ScannerView = ({ onResult, testid }) => {
  const { t } = useT();
  const elId = `scan-${useId().replace(/:/g, "")}`;
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const cb = useRef(onResult);
  cb.current = onResult;

  useEffect(() => {
    let scanner = null;
    let started = null;
    let done = false;
    // Delay start so StrictMode's mount/unmount cycle never starts two camera streams
    const timer = setTimeout(() => {
      scanner = new Html5Qrcode(elId, { verbose: false, experimentalFeatures: { useBarCodeDetectorIfSupported: true } });
      started = scanner
        .start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 280, height: 120 } }, (text) => {
          if (done) return;
          done = true;
          cb.current(text.trim());
        }, () => {})
        .then(() => true)
        .catch(() => {
          setError(t("camera_error"));
          return false;
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      if (started) {
        started.then((ok) => (ok ? scanner.stop().then(() => scanner.clear()) : null)).catch(() => {});
      }
    };
  }, [elId, t]);

  const submitManual = (e) => {
    e.preventDefault();
    if (manual.trim()) cb.current(manual.trim());
  };

  return (
    <div className="space-y-4">
      <div id={elId} data-testid={`${testid}-camera`} className="w-full min-h-[120px] bg-black rounded-sm overflow-hidden" />
      {error && <p data-testid={`${testid}-camera-error`} className="font-mono text-xs text-[#FFB800]">{error}</p>}
      <form onSubmit={submitManual} className="flex gap-2">
        <Input autoFocus data-testid={`${testid}-manual-input`} value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Barcode" className={fieldInputCls} />
        <Button type="submit" data-testid={`${testid}-manual-submit`} className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] h-10">{t("ok")}</Button>
      </form>
    </div>
  );
};

export const ScanButton = ({ onScan, testid }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={testid} title={t("scan_title")} onClick={() => setOpen(true)}
        className="h-10 w-10 shrink-0 flex items-center justify-center border border-[#282D3D] rounded-sm text-slate-400 hover:text-[#FFB800] hover:border-[#FFB800] transition-colors duration-200">
        <ScanLine className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-dialog`} className="bg-[#12141C] border-[#282D3D] rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl">{t("scan_title")}</DialogTitle>
            <DialogDescription>{t("scan_hint")}</DialogDescription>
          </DialogHeader>
          {open && <ScannerView testid={testid} onResult={(code) => { setOpen(false); onScan(code); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
